/** Height of the sticky shell header; a target scrolled under it would be hidden. */
const HEADER_OFFSET = 56;
/** Breathing room between the header (or a container edge) and a scrolled-to target. */
const SCROLL_GAP = 16;
const STILL_FRAMES = 3;

/** A stretch of one axis, in viewport coordinates. */
export interface Span {
  start: number;
  end: number;
}

/**
 * How far to scroll one axis so the target lies inside the area; 0 when it already does.
 * `start` and `center` (vertical) put a target taller than the area at its start. `nearest`
 * (horizontal) moves the least and leaves a target wider than the area alone: a wide table
 * should not jump sideways, but a button pushed off a page that overflows should come back.
 */
export function scrollDelta(
  target: { start: number; size: number },
  area: Span,
  align: 'start' | 'center' | 'nearest',
): number {
  const end = target.start + target.size;
  if (target.start >= area.start && end <= area.end) return 0;
  const room = area.end - area.start;
  if (align === 'nearest') {
    if (target.size > room) return 0;
    return target.start < area.start ? target.start - area.start : end - area.end;
  }
  if (align === 'start' || target.size > room) return target.start - area.start;
  return target.start + target.size / 2 - (area.start + area.end) / 2;
}

/** In the header, the sidebar or an open sheet: the page scroll does not move it. */
function inFixedLayer(element: HTMLElement): boolean {
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    const { position } = getComputedStyle(node);
    if (position === 'fixed' || position === 'sticky') return true;
  }
  return false;
}

const SCROLLS = /auto|scroll/;

function scrollAncestors(element: HTMLElement) {
  const found: Array<{ element: HTMLElement; x: boolean; y: boolean }> = [];
  for (
    let parent = element.parentElement;
    parent && parent !== document.body && parent !== document.documentElement;
    parent = parent.parentElement
  ) {
    const style = getComputedStyle(parent);
    const x = SCROLLS.test(style.overflowX) && parent.scrollWidth > parent.clientWidth;
    const y = SCROLLS.test(style.overflowY) && parent.scrollHeight > parent.clientHeight;
    if (x || y) found.push({ element: parent, x, y });
  }
  return found;
}

/**
 * Resolves once the element has not moved for a few frames (a sheet that slid in, a page that
 * finished scrolling), or after `maxMs`.
 */
export function waitForStableRect(element: HTMLElement, maxMs = 800): Promise<void> {
  return new Promise((resolve) => {
    let last = '';
    let still = 0;
    const started = performance.now();
    const tick = () => {
      const rect = element.getBoundingClientRect();
      const now = `${Math.round(rect.top)}:${Math.round(rect.left)}:${Math.round(rect.width)}:${Math.round(rect.height)}`;
      still = now === last ? still + 1 : 0;
      last = now;
      if (still >= STILL_FRAMES || performance.now() - started > maxMs) resolve();
      else requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

/**
 * Brings the target into view below the sticky header and resolves once scrolling has settled.
 * Scroll containers around the target move first, then the page. Replaces `scrollIntoView`,
 * which centres sideways too and so scrolled wide tables and overflowing pages to their middle.
 * On phones the step card covers part of the screen, so the target goes to the top half.
 */
export async function scrollTargetIntoView(
  element: HTMLElement,
  { reducedMotion, isMobile }: { reducedMotion: boolean; isMobile: boolean },
): Promise<void> {
  const block = isMobile ? 'start' : 'center';
  const viewportBottom = window.innerHeight - (isMobile ? window.innerHeight / 2 : 0);
  const viewportRight = document.documentElement.clientWidth || window.innerWidth;
  let moved = false;

  for (const container of scrollAncestors(element)) {
    const box = container.element.getBoundingClientRect();
    const top = box.top + container.element.clientTop;
    const left = box.left + container.element.clientLeft;
    const rect = element.getBoundingClientRect();
    const vertical = { start: Math.max(top, 0) + SCROLL_GAP, end: 0 };
    vertical.end = Math.min(top + container.element.clientHeight, viewportBottom) - SCROLL_GAP;
    const horizontal = {
      start: Math.max(left, 0),
      end: Math.min(left + container.element.clientWidth, viewportRight),
    };
    const dy =
      container.y && vertical.end > vertical.start
        ? scrollDelta({ start: rect.top, size: rect.height }, vertical, block)
        : 0;
    const dx = container.x
      ? scrollDelta({ start: rect.left, size: rect.width }, horizontal, 'nearest')
      : 0;
    if (dx !== 0 || dy !== 0) {
      container.element.scrollBy({ top: dy, left: dx, behavior: 'auto' });
      moved = true;
    }
  }

  if (!inFixedLayer(element)) {
    const rect = element.getBoundingClientRect();
    const dy = scrollDelta(
      { start: rect.top, size: rect.height },
      { start: HEADER_OFFSET + SCROLL_GAP, end: viewportBottom - SCROLL_GAP },
      block,
    );
    const dx = scrollDelta(
      { start: rect.left, size: rect.width },
      { start: SCROLL_GAP, end: viewportRight - SCROLL_GAP },
      'nearest',
    );
    if (dx !== 0 || dy !== 0) {
      window.scrollBy({ top: dy, left: dx, behavior: reducedMotion ? 'auto' : 'smooth' });
      moved = true;
    }
  }

  if (moved && !reducedMotion) await waitForStableRect(element);
}
